"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "default", {
    enumerable: true,
    get: function() {
        return User;
    }
});
const _nocodbsdk = require("nocodb-sdk");
const _catchError = require("~/helpers/catchError");
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
const _extractProps = require("~/helpers/extractProps");
const _NocoCache = /*#__PURE__*/ _interop_require_default(require("~/cache/NocoCache"));
const _globals = require("~/utils/globals");
const _models = require("~/models");
const _utils = require("~/utils");
const _emailUtils = require("~/utils/emailUtils");
const _modelUtils = require("~/utils/modelUtils");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
let User = class User {
    static castType(user) {
        return user && new User(user);
    }
    static async insert(user, ncMeta = _Noco.default.ncMeta) {
        const insertObj = (0, _extractProps.extractProps)(user, [
            'id',
            'email',
            'canonical_email',
            'password',
            'salt',
            'invite_token',
            'invite_token_expires',
            'reset_password_expires',
            'reset_password_token',
            'email_verification_token',
            'email_verified',
            'roles',
            'token_version',
            'is_new_user',
            'meta'
        ]);
        // Set is_new_user to true for new users if not explicitly set
        if (insertObj.is_new_user === undefined) {
            insertObj.is_new_user = true;
        }
        if (insertObj.email) {
            insertObj.email = insertObj.email.toLowerCase();
            insertObj.canonical_email = (0, _emailUtils.normalizeEmail)(insertObj.email);
        }
        const { id } = await ncMeta.metaInsert2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, (0, _modelUtils.prepareForDb)(insertObj));
        await _NocoCache.default.del('root', _globals.CacheScope.INSTANCE_META);
        // clear all base user related cache for instance
        const bases = await _models.Base.list(null, ncMeta);
        for (const base of bases){
            await _NocoCache.default.deepDel({
                workspace_id: base.fk_workspace_id,
                base_id: base.id
            }, `${_globals.CacheScope.BASE_USER}:${base.id}:list`, _globals.CacheDelDirection.PARENT_TO_CHILD);
        }
        return this.get(id, ncMeta);
    }
    static async update(id, user, ncMeta = _Noco.default.ncMeta) {
        const updateObj = (0, _extractProps.extractProps)(user, [
            'email',
            'canonical_email',
            'password',
            'salt',
            'invite_token',
            'invite_token_expires',
            'reset_password_expires',
            'reset_password_token',
            'email_verification_token',
            'email_verified',
            'roles',
            'token_version',
            'display_name',
            'avatar',
            'is_new_user',
            'meta'
        ]);
        if (updateObj.email) {
            updateObj.email = updateObj.email.toLowerCase();
            updateObj.canonical_email = (0, _emailUtils.normalizeEmail)(updateObj.email);
            // check if the target email addr is in use or not
            const targetUser = await this.getByEmail(updateObj.email, ncMeta);
            if (targetUser && targetUser.id !== id) {
                _catchError.NcError.badRequest('email is in use');
            }
            // check if a user with the same canonical email already exists
            const canonicalUser = await this.getByCanonicalEmail(updateObj.email, ncMeta);
            if (canonicalUser && canonicalUser.id !== id) {
                _catchError.NcError.badRequest('email is in use');
            }
        } else {
            var _email, _this;
            // set email prop to avoid generation of invalid cache key
            updateObj.email = (_this = await this.get(id, ncMeta)) == null ? void 0 : (_email = _this.email) == null ? void 0 : _email.toLowerCase();
        }
        // get old user
        const existingUser = await this.get(id, ncMeta);
        // delete the email-based cache to avoid unexpected behaviour since we can update email as well
        await _NocoCache.default.del('root', `${_globals.CacheScope.USER}:${existingUser.email}`);
        await ncMeta.metaUpdate(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, (0, _modelUtils.prepareForDb)(updateObj), id);
        // clear all user related cache
        await this.clearCache(id, ncMeta);
        return this.get(id, ncMeta);
    }
    static async getByEmail(_email, ncMeta = _Noco.default.ncMeta) {
        const email = _email == null ? void 0 : _email.toLowerCase();
        let user = email && await _NocoCache.default.get('root', `${_globals.CacheScope.USER}:${email}`, _globals.CacheGetType.TYPE_OBJECT);
        if (!user) {
            user = await ncMeta.metaGet2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, {
                email
            });
            if (user) {
                user.meta = (0, _modelUtils.parseMetaProp)(user);
            }
            await _NocoCache.default.set('root', `${_globals.CacheScope.USER}:${email}`, user);
        }
        if (user == null ? void 0 : user.is_deleted) {
            return null;
        }
        return this.castType(user);
    }
    /**
   * Look up a user by canonical (normalized) email.
   * Normalizes the input so any alias variant finds the right user.
   */ static async getByCanonicalEmail(_email, ncMeta = _Noco.default.ncMeta) {
        if (!_email || _email === '') return null;
        const canonical = (0, _emailUtils.normalizeEmail)(_email);
        let user = canonical && await _NocoCache.default.get('root', `${_globals.CacheScope.USER}:canonical:${canonical}`, _globals.CacheGetType.TYPE_OBJECT);
        if (!user) {
            user = await ncMeta.metaGet2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, {
                canonical_email: canonical
            });
            if (user) {
                user.meta = (0, _modelUtils.parseMetaProp)(user);
            }
            await _NocoCache.default.set('root', `${_globals.CacheScope.USER}:canonical:${canonical}`, user);
        }
        if (user == null ? void 0 : user.is_deleted) {
            return null;
        }
        return this.castType(user);
    }
    static async isFirst(ncMeta = _Noco.default.ncMeta) {
        return !await ncMeta.metaGet2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, {});
    }
    static async count({ query = '' } = {}, ncMeta = _Noco.default.ncMeta) {
        const qb = ncMeta.knex(_globals.MetaTable.USERS);
        if (query) {
            qb.where('email', 'like', `%${query.toLowerCase == null ? void 0 : query.toLowerCase.call(query)}%`);
        }
        return (await qb.count('id', {
            as: 'count'
        }).first()).count;
    }
    static async get(userId, ncMeta = _Noco.default.ncMeta) {
        let user = userId && await _NocoCache.default.get('root', `${_globals.CacheScope.USER}:${userId}`, _globals.CacheGetType.TYPE_OBJECT);
        if (!user) {
            user = await ncMeta.metaGet2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, userId);
            if (user) {
                user.meta = (0, _modelUtils.parseMetaProp)(user);
            }
            await _NocoCache.default.set('root', `${_globals.CacheScope.USER}:${userId}`, user);
        }
        if (user == null ? void 0 : user.is_deleted) {
            return null;
        }
        return this.castType(user);
    }
    static async getByRefreshToken(refresh_token, ncMeta = _Noco.default.ncMeta) {
        const userRefreshToken = await _models.UserRefreshToken.getByToken(refresh_token, ncMeta);
        if (!userRefreshToken) {
            return null;
        }
        const user = await ncMeta.metaGet2(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, userRefreshToken.fk_user_id);
        if (user == null ? void 0 : user.is_deleted) {
            return null;
        }
        if (user) {
            user.meta = (0, _modelUtils.parseMetaProp)(user);
        }
        return this.castType(user);
    }
    static async list({ limit, offset, query = '' } = {}, ncMeta = _Noco.default.ncMeta) {
        let queryBuilder = ncMeta.knex(_globals.MetaTable.USERS);
        if (offset) queryBuilder = queryBuilder.offset(offset);
        if (limit) queryBuilder = queryBuilder.limit(limit);
        queryBuilder = queryBuilder.select(`${_globals.MetaTable.USERS}.id`, `${_globals.MetaTable.USERS}.email`, `${_globals.MetaTable.USERS}.email_verified`, `${_globals.MetaTable.USERS}.invite_token`, `${_globals.MetaTable.USERS}.created_at`, `${_globals.MetaTable.USERS}.updated_at`, `${_globals.MetaTable.USERS}.roles`, `${_globals.MetaTable.USERS}.display_name`, `${_globals.MetaTable.USERS}.is_new_user`, `${_globals.MetaTable.USERS}.meta`).select(ncMeta.knex(_globals.MetaTable.PROJECT_USERS).count().whereRaw(`${_globals.MetaTable.USERS}.id = ${_globals.MetaTable.PROJECT_USERS}.fk_user_id`).as('projectsCount'));
        if (query) {
            queryBuilder.where(function() {
                this.where(function() {
                    this.whereNotNull('display_name').andWhereNot('display_name', '').andWhere('display_name', 'like', `%${query.toLowerCase()}%`);
                }).orWhere(function() {
                    this.where('email', 'like', `%${query.toLowerCase()}%`);
                });
            });
        }
        return queryBuilder;
    }
    static async delete(userId, ncMeta = _Noco.default.ncMeta) {
        if (!userId) _catchError.NcError.badRequest('userId is required');
        const user = await this.get(userId, ncMeta);
        if (!user) _catchError.NcError.userNotFound(userId);
        // clear all user related cache
        await this.clearCache(userId, ncMeta);
        return await ncMeta.metaDelete(_globals.RootScopes.ROOT, _globals.RootScopes.ROOT, _globals.MetaTable.USERS, userId);
    }
    static async getWithRoles(context, userId, args, ncMeta = _Noco.default.ncMeta) {
        var _args_user;
        const user = (_args_user = args.user) != null ? _args_user : await this.get(userId, ncMeta);
        if (!user) _catchError.NcError.userNotFound(userId);
        const baseRoles = await new Promise((resolve)=>{
            if (args.baseId) {
                _models.BaseUser.get(context, args.baseId, user.id, ncMeta).then(async (baseUser)=>{
                    const roles = baseUser == null ? void 0 : baseUser.roles;
                    // + (user.roles ? `,${user.roles}` : '');
                    if (roles) {
                        // If role is INHERIT (can be 'inherit' string), treat it as null to fall back to workspace roles
                        // Since INHERIT at base level means inherit from workspace level
                        if (roles === _nocodbsdk.ProjectRoles.INHERIT) {
                            resolve(null);
                        } else {
                            resolve((0, _nocodbsdk.extractRolesObj)(roles));
                        }
                    } else {
                        resolve(null);
                    }
                // todo: cache
                });
            } else {
                resolve(null);
            }
        });
        return _extends({}, (0, _utils.sanitiseUserObj)(user), {
            roles: user.roles ? (0, _nocodbsdk.extractRolesObj)(user.roles) : null,
            base_roles: baseRoles ? baseRoles : null
        });
    }
    static async clearCache(userId, ncMeta = _Noco.default.ncMeta) {
        const user = await this.get(userId, ncMeta);
        if (!user) _catchError.NcError.userNotFound(userId);
        // todo: skip base user cache delete based on flag
        const bases = await _models.BaseUser.getProjectsList(userId, {}, ncMeta);
        for (const base of bases){
            await _NocoCache.default.deepDel({
                workspace_id: base.fk_workspace_id,
                base_id: base.id
            }, `${_globals.CacheScope.BASE_USER}:${base.id}:list`, _globals.CacheDelDirection.PARENT_TO_CHILD);
        }
        // clear all user related cache
        await _NocoCache.default.del('root', `${_globals.CacheScope.USER}:${userId}`);
        await _NocoCache.default.del('root', `${_globals.CacheScope.USER}:${user.email}`);
        if (user.email) {
            await _NocoCache.default.del('root', `${_globals.CacheScope.USER}:canonical:${(0, _emailUtils.normalizeEmail)(user.email)}`);
        }
    }
    static async signUserImage(users) {
        if (!users) return;
        const promises = [];
        try {
            for (const user of Array.isArray(users) ? users : [
                users
            ]){
                if (!(0, _nocodbsdk.ncIsObject)(user)) {
                    continue;
                }
                user.meta = (0, _modelUtils.parseMetaProp)(user);
                if (user.meta && user.meta.icon && user.meta.iconType === _nocodbsdk.IconType.IMAGE) {
                    promises.push(_models.PresignedUrl.signAttachment({
                        attachment: user.meta.icon
                    }));
                }
            }
            await Promise.all(promises);
        } catch (unused) {}
    }
    constructor(data){
        Object.assign(this, data);
    }
};
